import styled, { css } from 'styled-components';
import WaBadge from '@web.awesome.me/webawesome-pro/dist/react/badge';

// Light theme: subtle tint bg + dark colored text (good contrast on white page).
const quietTokens = css`
  &[variant='brand'] {
    --wa-color-brand-fill-normal: var(--wa-color-brand-fill-quiet);
    --wa-color-brand-on-normal: var(--wa-color-brand-on-quiet);
  }
  &[variant='success'] {
    --wa-color-success-fill-normal: var(--wa-color-success-fill-quiet);
    --wa-color-success-on-normal: var(--wa-color-success-on-quiet);
  }
  &[variant='warning'] {
    --wa-color-warning-fill-normal: var(--wa-color-warning-fill-quiet);
    --wa-color-warning-on-normal: var(--wa-color-warning-on-quiet);
  }
  &[variant='danger'] {
    --wa-color-danger-fill-normal: var(--wa-color-danger-fill-quiet);
    --wa-color-danger-on-normal: var(--wa-color-danger-on-quiet);
  }
`;

// Dark theme: inverted — colored bg stays tinted, text + icon get lighter.
const darkTokens = css`
  html.wa-dark &[variant='brand'] {
    --wa-color-brand-fill-normal: color-mix(
      in srgb,
      var(--wa-color-brand-fill-loud) 20%,
      transparent
    );
    --wa-color-brand-on-normal: color-mix(in srgb, var(--wa-color-brand-fill-loud) 40%, #ffffff);
  }
  html.wa-dark &[variant='success'] {
    --wa-color-success-fill-normal: color-mix(
      in srgb,
      var(--wa-color-success-fill-loud) 20%,
      transparent
    );
    --wa-color-success-on-normal: color-mix(
      in srgb,
      var(--wa-color-success-fill-loud) 40%,
      #ffffff
    );
  }
  html.wa-dark &[variant='warning'] {
    --wa-color-warning-fill-normal: color-mix(
      in srgb,
      var(--wa-color-warning-fill-loud) 20%,
      transparent
    );
    --wa-color-warning-on-normal: color-mix(
      in srgb,
      var(--wa-color-warning-fill-loud) 40%,
      #ffffff
    );
  }
  html.wa-dark &[variant='danger'] {
    --wa-color-danger-fill-normal: color-mix(
      in srgb,
      var(--wa-color-danger-fill-loud) 20%,
      transparent
    );
    --wa-color-danger-on-normal: color-mix(in srgb, var(--wa-color-danger-fill-loud) 40%, #ffffff);
  }
  html.wa-dark &[variant='neutral'] {
    --wa-color-neutral-fill-normal: color-mix(
      in srgb,
      var(--wa-color-neutral-fill-loud) 20%,
      transparent
    );
    --wa-color-neutral-on-normal: color-mix(
      in srgb,
      var(--wa-color-neutral-fill-loud) 40%,
      #ffffff
    );
  }
`;

export const Badge = styled(WaBadge)`
  /* WA 3.9's badge floors its font-size at --wa-font-size-3xs, a step smaller
     than the --wa-font-size-2xs used before the Pro upgrade, so every badge
     shrank (and its em-based padding with it). Restore the 2xs floor to match
     the pre-Pro size and padding. */
  font-size: max(var(--wa-font-size-2xs), 0.75em);
  /* Never let a narrow (flex) container squeeze the badge — it keeps its
     natural width and its label on one line instead of being compressed. */
  flex-shrink: 0;
  max-width: 100%;
  overflow: hidden;
  ${({ $uppercase }) => $uppercase && 'text-transform: uppercase;'}

  /* The label lives in part(base), so clipping has to happen there — on the
     host it just overruns the badge. Only kicks in once the badge is actually
     constrained by its container. */
  &&::part(base) {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  svg,
  wa-icon {
    font-size: 0.85em;
    opacity: 0.85;
    /* Space the icon from the label. WA 3.9 lays slotted content out inside the
       shadow base part, so a host-level gap no longer reaches it; set the
       spacing on the (slotted) icon itself instead. */
    margin-inline-end: 0.5em;
  }

  ${({ $filled }) => !$filled && quietTokens}
  ${({ $filled }) => !$filled && darkTokens}
`;
